import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Globe, Palette, Settings, Database, Shield, Zap, Search,
  ShoppingCart, Code, Server, Monitor, Smartphone, Mail,
  FileText, Layout, Layers, BarChart, Cloud,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Globe, Palette, Settings, Database, Shield, Zap, Search,
  ShoppingCart, Code, Server, Monitor, Smartphone, Mail,
  FileText, Layout, Layers, BarChart, Cloud,
};

// Grid spans based on position and count
const getSpans = (count) => {
  if (count <= 2) return Array(count).fill("md:col-span-6");
  if (count === 3) return ["md:col-span-6", "md:col-span-6", "md:col-span-6"];
  if (count === 4) return ["md:col-span-6", "md:col-span-6", "md:col-span-6", "md:col-span-6"];
  if (count === 5) return ["md:col-span-6", "md:col-span-6", "md:col-span-4", "md:col-span-4", "md:col-span-4"];
  if (count === 6) return ["md:col-span-6", "md:col-span-6", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-12"];
  // 7 items: 2 + 3 + 2
  return ["md:col-span-6", "md:col-span-6", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-6", "md:col-span-6"];
};

export const ServicesSection = ({ location = "homepage" }) => {
  const [boxes, setBoxes] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/service-boxes?location=${location}`)
      .then((res) => setBoxes(res.data))
      .catch(console.error);
  }, [location]);

  if (boxes.length === 0) return null;

  const spans = getSpans(boxes.length);

  return (
    <section data-testid="services-section" className="section-padding bg-its-teal">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="label-text">What We Do</span>
          <h2 className="heading-2 mt-2">Our Services</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {boxes.map((box, index) => {
            const IconComponent = ICON_MAP[box.icon] || Globe;
            return (
              <motion.div
                key={box.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`glass-card group hover:border-its-gold/50 transition-all duration-300 hover:-translate-y-1 ${spans[index] || "md:col-span-4"}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-its-gold/10 text-its-gold group-hover:bg-its-gold group-hover:text-its-teal transition-all">
                    <IconComponent size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {box.title}
                    </h3>
                    <p className="text-its-offwhite/80 text-sm leading-relaxed">
                      {box.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* "Learn more" link — homepage only */}
        {location === "homepage" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-10"
          >
            <Link
              to="/services"
              onClick={() => window.scrollTo({ top: 0 })}
              className="text-its-gold hover:text-its-gold-soft transition-colors font-medium underline underline-offset-4"
              data-testid="learn-more-services-link"
            >
              Learn more about our services.
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};
